import {
  ButtonBase,
  IconButton,
  Paper,
  Typography,
  Fade
} from '@material-ui/core'
import LeftArrowIcon from '@material-ui/icons/ChevronLeft'
import RightArrowIcon from '@material-ui/icons/ChevronRight'
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DateRangePicker } from '../../components/DatePicker/DateRangePicker'
import {
  formatDateFullObject,
  getDatesList,
  addDaysToDate,
  getTodayDate,
  DateObject,
  isWeekend
} from '../../helpers/date'
import { lang } from '../../localization'
import useStyles from './styles/InventoryDateRow.styles'

interface InventoryDateRowProps {
  selectedDateInDatePicker: DateObject
  setselectedDateInDatePicker: React.Dispatch<React.SetStateAction<DateObject>>
  range?: 'TWO_WEEKS' | 'MONTH'
}

const InventoryDateRow: React.FC<InventoryDateRowProps> = ({
  selectedDateInDatePicker,
  setselectedDateInDatePicker,
  range = 'TWO_WEEKS'
}) => {
  const classes = useStyles()

  const perPage = useMemo(() => (range === 'TWO_WEEKS' ? 14 : 31), [range])

  const [isOpen, setisOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setisOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isOpen])

  const datesList = useMemo(
    () => getDatesList(perPage, selectedDateInDatePicker.gregorianStartDate),
    [perPage, selectedDateInDatePicker.gregorianStartDate]
  )

  return (
    <Paper
      className={
        range === 'TWO_WEEKS' ? classes.paperWeekly : classes.paperMonthly
      }
      elevation={5}>
      <div className={classes.bigDateContainer}>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() => {
            const newFirstDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              -perPage
            )
            const today = getTodayDate()
            const newGregorianStartDate =
              newFirstDate > today ? newFirstDate : today
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: newGregorianStartDate,
              gregorianEndDate: addDaysToDate(newGregorianStartDate, 13)
            })
          }}>
          <DoubleArrowIcon
            className={[classes.prevPageIcon, classes.arrowIcon].join(' ')}
            fontSize="small"
          />
        </IconButton>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                -1
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                -1
              )
            })
          }>
          <LeftArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        {datesList && datesList.length > 0 && (
          <div className={classes.bidDateContainer} ref={wrapperRef}>
            <ButtonBase onClick={() => setisOpen(!isOpen)}>
              <Typography
                variant="h3"
                color="primary"
                className={classes.bigDate}>
                {formatDateFullObject(datesList[0])}
              </Typography>
            </ButtonBase>
            {isOpen && (
              <Fade in={isOpen}>
                <div className={classes.datePickerContainer}>
                  <DateRangePicker
                    // single Date---------
                    // onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    // dateObject={selectedDateInDatePicker}
                    // range={false}
                    // default(range):---------
                    // onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    // dateObject={selectedDateInDatePicker}
                    // auto select range:---------
                    onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    dateObject={selectedDateInDatePicker}
                    autoSelectedEndDate
                    numberOfHoveredItems={perPage}
                    type={lang === 'fa' ? 'jalaali' : 'gregorian'}
                    onDoneClicked={() => setisOpen(false)}
                    // multiple select:--------------
                    // onChange={(d: DateObject) =>
                    //   setdateObjectList((currentList) => [...currentList, d])
                    // }
                    // multipleDates
                    // dateObjectList={dateObjectList}
                  />
                </div>
              </Fade>
            )}
          </div>
        )}
        <IconButton
          size="small"
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                1
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                1
              )
            })
          }>
          <RightArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() =>
            setselectedDateInDatePicker({
              ...selectedDateInDatePicker,
              gregorianStartDate: addDaysToDate(
                selectedDateInDatePicker.gregorianStartDate!,
                perPage
              ),
              gregorianEndDate: addDaysToDate(
                selectedDateInDatePicker.gregorianEndDate!,
                perPage
              )
            })
          }>
          <DoubleArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
      </div>
      {datesList.map((item) => (
        <div
          className={[
            classes.dayContainer,
            isWeekend(item) ? classes.weekendDayContainer : '',
            range === 'MONTH' ? classes.dayContainerSmall : ''
          ].join(' ')}
          key={item.gregorianDate.date}>
          <span>
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].shortMonth}
          </span>
          <Typography variant="h4">
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].monthDay}
          </Typography>
          <span>{item.gregorianDate.weekDay}</span>
        </div>
      ))}
    </Paper>
  )
}

export default InventoryDateRow
